
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { AccordionItem } from '@/components/core/Accordion';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { useAreAllAttributesHidden } from '@/components/datamodel/hooks/useIsHiddenAttribute';
import { LayoutSection } from '@/types/DataModel';

import { SectionAccordionContent } from './SectionAccordionContent';
import { SectionAccordionTrigger } from './SectionAccordionTrigger';

const CoreSection = ({ section, isOpen, primaryKey, scrollIntoViewOnOpen }: { section: LayoutSection, isOpen: boolean, primaryKey?: string, scrollIntoViewOnOpen?: boolean }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);

    const isAllAttributesHidden = useAreAllAttributesHidden(section.attributes.map(att => att.name));

    useEffect(() => {
        if (isOpen) {
            setHasBeenOpened(true);
        }
    }, [isOpen]);

    const wasOpen = useRef(isOpen);
    useEffect(() => {
        const openedByUser = isOpen && !wasOpen.current;
        wasOpen.current = isOpen;
        if (scrollIntoViewOnOpen && openedByUser && sectionRef.current) {
            sectionRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }, [isOpen, scrollIntoViewOnOpen]);

    // Lazy-mount attributes on first open, then keep them mounted across closes.
    const attributeComponents = useMemo(() => {
        if (!hasBeenOpened) {
            return null;
        }

        return section.attributes.map(attribute =>
            <AttributeComponent key={`${section.name}-${attribute.name}`} attribute={attribute} primaryKey={primaryKey} />
        );
    }, [hasBeenOpened, section.attributes, section.name, primaryKey]);

    if (isAllAttributesHidden) {
        return null;
    }

    return (
        <AccordionItem ref={sectionRef} value={section.name} data-section-name={section.name} className='react-panel'>
            <div className={`react-panel-collapse react-collapse ${isOpen ? 'in' : ''}`}>
                <div className="tw-sticky tw-top-[-15px] tw-z-20 tw-bg-white">
                    <SectionAccordionTrigger section={section} id={(section.label || section.name).replace(/\s+/g, '-').toLowerCase()}/>
                </div>
                <SectionAccordionContent isOpen={isOpen} keepMounted={hasBeenOpened}>
                    {attributeComponents}
                </SectionAccordionContent>
            </div>
        </AccordionItem>
    );
};

CoreSection.DisplayName = 'Section';

const Section = memo(CoreSection);

export { Section };
